/** Error shape the web `Page` component knows how to render. */
export interface EventPageError {
  /** 403 renders the private-entity screen; 404 renders "Not Found". */
  status: number
  message: string
}

interface ResolveEventErrorArgs {
  /** The thrown query error, if the request failed. */
  error?: unknown
  /** The resolved event — `null`/`undefined` once settled means it is gone. */
  event?: unknown
  /** True while the request is still in flight. */
  isLoading: boolean
}

/** Copy shown when an event is missing — deleted, or never existed. */
export const EVENT_MISSING_MESSAGE =
  'This event no longer exists. It may have been deleted.'

/** Copy shown when the request failed without saying why. */
export const EVENT_GENERIC_ERROR_MESSAGE = 'Something went wrong.'

/** Copy shown when the event loaded but the viewer may not read it. */
export const EVENT_FORBIDDEN_MESSAGE =
  "You don't have permission to view this content."

/**
 * The page error for an event the viewer cannot read.
 *
 * 403 so the platform's existing forbidden layout renders it — the same screen
 * every other private entity uses, rather than a second one-off.
 */
export const EVENT_FORBIDDEN_ERROR: EventPageError = {
  status: 403,
  message: EVENT_FORBIDDEN_MESSAGE,
}

/**
 * Status code carried by a failed API response.
 *
 * `statusCode` is checked FIRST because that is the field
 * `common/api/request` actually sets on an error. Reading only `status` (as
 * the event page originally did) meant a genuine 403 was treated as a 404 and
 * rendered "Not Found" instead of the private-entity screen. `status` is still
 * accepted for callers and mocks that set it instead.
 */
export const getErrorStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined
  }
  const { status, statusCode } = error as {
    status?: unknown
    statusCode?: unknown
  }
  if (typeof statusCode === 'number') {
    return statusCode
  }
  return typeof status === 'number' ? status : undefined
}

/**
 * Normalize an event query's outcome into the error the page should render.
 *
 * Two distinct failure paths converge here:
 * - a THROWN request error (403 private, 5xx, network), and
 * - a settled, event-less response: `getEvent` resolves to `null` for a
 *   missing event rather than throwing, so react-query reports success and a
 *   DELETED event would otherwise render an empty page with no error at all.
 *
 * Returns null while loading or when the event resolved normally.
 */
export const resolveEventError = ({
  error,
  event,
  isLoading,
}: ResolveEventErrorArgs): EventPageError | null => {
  if (error) {
    const message =
      (error as { message?: string })?.message || EVENT_GENERIC_ERROR_MESSAGE
    return { status: getErrorStatus(error) ?? 404, message }
  }

  if (!isLoading && !event) {
    return { status: 404, message: EVENT_MISSING_MESSAGE }
  }

  return null
}
